import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Check, Star, Sparkles, Clock, Loader2, Settings2, ArrowRight, CreditCard, Receipt } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BillingStatusCard } from "@/components/BillingStatusCard";
import { PlanPriceEditor } from "@/components/PlanPriceEditor";
import { InvoiceHistory } from "@/components/InvoiceHistory";

const TIERS = [
  {
    slug: "basic-broadcaster",
    name: "Basic Broadcaster",
    price: "49.95",
    tagline: "Everything a growing ministry needs to go live.",
    features: [
      "Unlimited Viewer Access",
      "Stream to Facebook and YouTube",
      "24 Hour Support",
      "Ad-Free Broadcasting",
      "Embed Code for Your Website",
      "Roku & Fire TV Feed Generation",
    ],
  },
  {
    slug: "media-ministry-hub",
    name: "Media Ministry Hub",
    price: "59.95",
    popular: true,
    tagline: "Everything in Basic, plus a full media presence.",
    inheritsFrom: "Basic Broadcaster",
    features: [
      "Free Website with Online Giving",
      "Premier Support Services",
      "Archive Your Streams (On-Demand Library)",
      "Dedicated Client Portal",
    ],
  },
  {
    slug: "premium-services",
    name: "Premium Services",
    price: "89.95",
    tagline: "The complete broadcast toolkit for larger ministries.",
    inheritsFrom: "Basic & Media Ministry Hub",
    features: [
      "Share to Rumble & Amazon Fire Channel",
      "Your Own Roku Channel",
      "Ad-Free Broadcasting",
      "SD or HD Streaming",
      "And even more!",
    ],
  },
  {
    slug: "ascend-ultra",
    name: "Ascend Ultra",
    price: "99.95",
    tagline: "Cinema-grade 4K broadcasting for flagship ministries.",
    inheritsFrom: "Premium Services",
    features: [
      "Full 1080p / 4K Broadcast Support",
      "8 Mbps High-Bitrate Streaming",
      "Priority 4K Cloud Archiving",
      "Unlimited Video Storage",
      "VIP Priority Support",
    ],
  },
];

function TrialStatus({ user }) {
  if (!user?.trial_ends_at) return null;
  const days = Math.ceil((new Date(user.trial_ends_at).getTime() - Date.now()) / 86400000);
  const expired = days <= 0;
  return (
    <div data-testid="billing-trial-status" className={"inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm " + (expired ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300")}>
      <Clock className="h-4 w-4" />
      {expired ? "Your free trial has ended" : `${days} day${days === 1 ? "" : "s"} left in your free trial`}
    </div>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/billing/state").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get("checkout") === "cancelled") toast.info("Checkout cancelled — you haven't been charged.");
  }, []);

  const prices = state?.prices || {};
  const canSeeInvoices = !isAdmin && (!user?.is_sub_account || user?.access_level === "manager");
  const subscribed = state?.has_stripe && ["active", "trialing", "past_due", "unpaid", "incomplete"].includes(state?.stripe_status);
  const currentPlan = subscribed ? state?.plan : (["legacy", "comp"].includes(state?.reason) ? user?.plan : "");

  const openPortal = async () => {
    setBusy("portal");
    try {
      const r = await api.post("/billing/stripe/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't open billing portal"); setBusy(null); }
  };

  const choose = async (tier) => {
    if (subscribed) return openPortal();
    setBusy(tier.slug);
    try {
      const r = await api.post("/billing/stripe/checkout", { plan_id: tier.slug, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start checkout. Please try again."); setBusy(null); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Plans &amp; Billing</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Choose the plan that fits your ministry. Billed monthly through Stripe — change or cancel anytime.</p>
          <div className="mt-4"><TrialStatus user={null} /></div>
        </div>
        {isAdmin && (
          <button data-testid="edit-plan-prices" onClick={() => setEditing((e) => !e)} className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-medium border border-gray-700 text-gray-200 hover:border-amber-500/50 hover:text-amber-300 transition-colors">
            <Settings2 className="h-4 w-4" /> {editing ? "Close price editor" : "Edit plan prices"}
          </button>
        )}
      </div>

      {isAdmin && editing && <PlanPriceEditor tiers={TIERS} onSaved={load} />}
      {!isAdmin && state?.pricing_override === "comp" && (
        <div data-testid="billing-comp-banner" className="mb-6 rounded-2xl border border-sky-500/30 bg-sky-500/10 px-5 py-4 text-sm text-sky-200">Your account is complimentary — you won't be charged, and all your plan features stay active.</div>
      )}
      {!isAdmin && state?.pricing_override === "custom" && (
        <div data-testid="billing-custom-banner" className="mb-6 rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/10 px-5 py-4 text-sm text-fuchsia-200">You have a custom rate of ${Number(state.custom_price).toFixed(2)} per {state.custom_interval === "year" ? "year" : "month"}, applied to whichever plan you choose.</div>
      )}
      {!isAdmin && state && state.pricing_override !== "comp" && <BillingStatusCard state={state} onPortal={openPortal} portalBusy={busy === "portal"} />}

      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4 items-start">
        {TIERS.map((tier, i) => {
          const isCurrent = tier.slug === currentPlan;
          const price = prices[tier.slug] || tier.price;
          return (
            <motion.div
              key={tier.slug}
              data-testid={`billing-card-${tier.slug}`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`relative rounded-2xl p-8 flex flex-col ${tier.popular ? "border-2 border-amber-500/60 bg-gradient-to-b from-amber-500/[0.08] to-transparent shadow-[0_0_60px_rgba(245,158,11,0.12)]" : "border border-gray-800 bg-card"}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 gold-gradient text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  <Star className="h-3.5 w-3.5 fill-black" /> MOST POPULAR
                </div>
              )}
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-bold">{tier.name}</h3>
                {isCurrent && <span data-testid={`billing-current-${tier.slug}`} className="text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Current plan</span>}
              </div>
              <p className="mt-2 text-sm text-gray-400 min-h-[40px]">{tier.tagline}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span data-testid={`billing-price-${tier.slug}`} className="text-4xl font-display font-bold text-gold-gradient">${price}</span>
                <span className="text-gray-500 text-sm">/mo</span>
              </div>

              {!isAdmin && (
                <button
                  data-testid={`billing-cta-${tier.slug}`}
                  onClick={() => choose(tier)}
                  disabled={(isCurrent && !subscribed) || !!busy}
                  className={`mt-6 w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60 ${isCurrent ? "bg-gray-800 text-gray-300" : tier.popular ? "gold-gradient text-black hover:brightness-110 shadow-lg shadow-amber-500/20" : "border border-gray-700 text-gray-100 hover:border-amber-500/50 hover:text-white"}`}
                >
                  {busy === tier.slug ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening Stripe…</>
                    : isCurrent && subscribed ? <><CreditCard className="h-4 w-4" /> Manage plan</>
                    : isCurrent ? "Your current plan"
                    : subscribed ? <>Switch plan <ArrowRight className="h-4 w-4" /></>
                    : <>Subscribe ${price}/mo <ArrowRight className="h-4 w-4" /></>}
                </button>
              )}

              {tier.inheritsFrom && (
                <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-amber-400">Everything in {tier.inheritsFrom}, plus:</p>
              )}
              <ul className={`space-y-3 ${tier.inheritsFrom ? "mt-3" : "mt-6"}`}>
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center shrink-0">
                      <Check className="h-3 w-3 text-amber-400" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>

      {canSeeInvoices && (
        <div data-testid="billing-invoices" className="mt-10 rounded-2xl border border-gray-800 bg-card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800 flex items-center gap-2">
            <Receipt className="h-4 w-4 text-amber-400" />
            <h2 className="font-display font-semibold text-sm">Your Invoices</h2>
          </div>
          <InvoiceHistory self />
        </div>
      )}

      <p className="mt-8 text-xs text-gray-500 flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-amber-400" />
        Payments are processed securely by Stripe. You're charged monthly and can update your card, switch plans or cancel anytime from Manage billing.
      </p>
    </div>
  );
}
